import { Button, Divider, Tooltip } from "@heroui/react";
import type { Editor } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useState,
} from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Redo,
  SeparatorHorizontal,
  Strikethrough,
  Undo,
  Link as LinkIcon,
  Image as ImageIcon,
} from "lucide-react";
import { t } from "@lingui/core/macro";
import {
  NotificationTag,
  NotificationTagOption,
  getNotificationTagDisplayValue,
} from "./extensions/NotificationTag";
import { NotificationTagSlashCommand } from "./extensions/NotificationTagSlashCommand";

export type MarkdownEditorRef = Editor;

export type MarkdownEditorActions =
  | "bold"
  | "italic"
  | "strike"
  | "link"
  | "image"
  | "unordered-list"
  | "ordered-list"
  | "horizontal-rule"
  | "paragraph"
  | "undo"
  | "redo";

export const MarkdownEditor = forwardRef<
  MarkdownEditorRef,
  {
    value?: string;
    onChange: (value: string) => void;
    dataIdentifier?: string;
    excludeActions?: MarkdownEditorActions[];
    tagOptions?: NotificationTagOption[];
    onSlashMenuOpen?: () => void;
    onSlashMenuClose?: () => void;
    isDisabled?: boolean;
    /**
     * Applied to every chunk of text the user types or pastes; the returned
     * string is inserted instead. Used to keep characters out of the editor
     * entirely (e.g. emoji in SMS bodies). Omit to insert input verbatim.
     */
    filterInsertedText?: (text: string) => string;
  }
>(
  (
    {
      value,
      onChange,
      dataIdentifier,
      excludeActions,
      tagOptions,
      onSlashMenuOpen,
      onSlashMenuClose,
      isDisabled,
      filterInsertedText,
    },
    ref,
  ) => {
    const normalizedValue = value ?? "";

    const tagValuesKey = useMemo(
      () => tagOptions?.map((tag) => tag.value).join("|") ?? "",
      [tagOptions],
    );

    const stableTagOptions = useMemo(
      () => tagOptions ?? [],
      [tagValuesKey, tagOptions],
    );

    const [initialContent] = useState(() => {
      const content = stableTagOptions.length
        ? convertPlainHtmlToEditorHtml(normalizedValue, stableTagOptions)
        : normalizedValue;
      return content;
    });

    const notificationTagExtension = useMemo(() => {
      if (!stableTagOptions.length) {
        return null;
      }
      return NotificationTag.configure({
        tags: stableTagOptions,
      });
    }, [stableTagOptions]);

    const slashCommandExtension = useMemo(() => {
      if (!stableTagOptions.length) return null;
      return NotificationTagSlashCommand.configure({
        tags: stableTagOptions,
        onMenuOpen: onSlashMenuOpen,
        onMenuClose: onSlashMenuClose,
      });
    }, [stableTagOptions, onSlashMenuOpen, onSlashMenuClose]);

    const editor = useEditor({
      editable: !isDisabled,
      extensions: [
        StarterKit.configure({
          bulletList: {
            HTMLAttributes: {
              class: "list-disc ml-4",
            },
          },
          orderedList: {
            HTMLAttributes: {
              class: "list-decimal ml-4",
            },
          },
        }),
        Highlight,
        ...(excludeActions?.includes("link")
          ? []
          : [
              Link.configure({
                autolink: true,
                linkOnPaste: true,
                openOnClick: false,
                HTMLAttributes: {
                  rel: "noopener noreferrer",
                  target: "_blank",
                  class: "text-blue-500 underline cursor-pointer",
                },
                protocols: ["http", "https", "mailto", "tel"],
              }),
            ]),
        ...(excludeActions?.includes("image")
          ? []
          : [Image.configure({ inline: false, allowBase64: false })]),
        ...(notificationTagExtension ? [notificationTagExtension] : []),
        ...(slashCommandExtension ? [slashCommandExtension] : []),
      ],
      content: initialContent,
      onUpdate: ({ editor }) => {
        if (!editor.getText()) {
          onChange("");
          return;
        }

        const html = editor.getHTML();
        const sanitizedHtml =
          stableTagOptions.length && canUseDom()
            ? stripNotificationTagsFromHtml(html)
            : html;
        onChange(sanitizedHtml);
      },
      editorProps: {
        attributes: {
          class: "outline-hidden",
        },
        // Block disallowed characters at the source: typed input runs
        // through the filter before insertion, and pastes (plain or rich)
        // have every text node filtered. Returning the filtered text from
        // handleTextInput means a fully-rejected char never reaches the doc.
        ...(filterInsertedText
          ? {
              handleTextInput(view, from, to, text) {
                const filtered = filterInsertedText(text);
                if (filtered === text) return false;
                if (filtered) {
                  view.dispatch(view.state.tr.insertText(filtered, from, to));
                }
                return true;
              },
              transformPasted(slice) {
                return filterSliceText(slice, filterInsertedText);
              },
            }
          : {}),
      },
    });

    useImperativeHandle(ref, () => editor as Editor, [editor]);

    useEffect(() => {
      if (!editor || editor.isDestroyed) return;

      const editorHtml = editor.getHTML();
      const strippedEditorHtml =
        stableTagOptions.length && canUseDom()
          ? stripNotificationTagsFromHtml(editorHtml)
          : editorHtml;

      if (strippedEditorHtml === normalizedValue) {
        return;
      }

      const nextContent =
        stableTagOptions.length && canUseDom()
          ? convertPlainHtmlToEditorHtml(normalizedValue, stableTagOptions)
          : normalizedValue;

      editor.commands.setContent(nextContent);
    }, [normalizedValue, editor, tagValuesKey, stableTagOptions]);

    return (
      <div className="rounded-lg border-1 border-gray-200 p-2">
        <MenuBar editor={editor} excludeActions={excludeActions} />

        <div className="p-1">
          <EditorContent editor={editor} data-identifier={dataIdentifier} />
        </div>
      </div>
    );
  },
);

MarkdownEditor.displayName = "MarkdownEditor";

const MenuBar = ({
  editor,
  excludeActions,
}: {
  editor: Editor;
  excludeActions?: MarkdownEditorActions[];
}) => {
  if (!editor) {
    return null;
  }

  const actions = {
    ["bold"]: {
      icon: <Bold size={16} />,
      onClick: () => editor.chain().focus().toggleBold().run(),
      tooltip: t`Krepko`,
    },
    ["italic"]: {
      icon: <Italic size={16} />,
      onClick: () => editor.chain().focus().toggleItalic().run(),
      tooltip: t`Poševno`,
    },
    ["strike"]: {
      icon: <Strikethrough size={16} />,
      onClick: () => editor.chain().focus().toggleStrike().run(),
      tooltip: t`Prečrtano`,
    },
    ["link"]: {
      icon: <LinkIcon size={16} />,
      onClick: () => {
        const { from, to } = editor.state.selection;
        const selectedText = editor.state.doc.textBetween(from, to, "");
        const previousUrl = editor.getAttributes("link").href as
          | string
          | undefined;

        // If we're on an existing link with no selection, allow editing or removing it
        if (previousUrl && !selectedText) {
          const url = window.prompt("URL", previousUrl);

          if (url === null) return; // cancelled

          if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
          }

          // Validate URL against allowed protocols
          try {
            const parsedUrl = new URL(url);
            const allowedProtocols = ["http:", "https:", "mailto:", "tel:"];
            if (!allowedProtocols.includes(parsedUrl.protocol)) {
              alert(
                t`Neveljaven URL. Dovoljeni protokoli: ${allowedProtocols.join(", ")}`,
              );
              return;
            }
          } catch {
            alert(t`Neveljaven URL format`);
            return;
          }

          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
          return;
        }

        // If no text is selected, ask for display text first
        let linkText = selectedText;
        if (!linkText) {
          const text = window.prompt(t`Vpiši besedilo za prikaz`);
          if (!text) return; // cancelled
          linkText = text;
        }

        // Ask for the URL
        const url = window.prompt("URL", previousUrl || "https://");

        if (url === null) return; // cancelled

        if (url === "") {
          if (selectedText) {
            // Remove link from selected text
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
          }
          return;
        }

        // Validate URL against allowed protocols
        try {
          const parsedUrl = new URL(url);
          const allowedProtocols = ["http:", "https:", "mailto:", "tel:"];
          if (!allowedProtocols.includes(parsedUrl.protocol)) {
            alert(
              t`Neveljaven URL. Dovoljeni protokoli: ${allowedProtocols.join(", ")}`,
            );
            return;
          }
        } catch {
          alert(t`Neveljaven URL format`);
          return;
        }

        // Apply the link
        if (selectedText) {
          // Text was already selected, just apply the link
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
        } else {
          // Insert new text with link
          editor
            .chain()
            .focus()
            .insertContent({
              type: "text",
              text: linkText,
              marks: [{ type: "link", attrs: { href: url } }],
            })
            .run();
        }
      },
      tooltip: t`Povezava`,
    },
    ["image"]: {
      icon: <ImageIcon size={16} />,
      onClick: () => {
        const url = window.prompt(t`Vpiši URL slike (https://...)`);
        if (!url) return;

        // Validate URL format and protocol
        try {
          const parsedUrl = new URL(url);
          const allowedProtocols = ["http:", "https:"];
          if (!allowedProtocols.includes(parsedUrl.protocol)) {
            alert(t`Neveljaven URL. Dovoljene so samo http/https slike`);
            return;
          }
        } catch {
          alert(t`Neveljaven URL format`);
          return;
        }

        editor.chain().focus().setImage({ src: url }).run();
      },
      tooltip: t`Slika`,
    },
    ["unordered-list"]: {
      icon: <List size={16} />,
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      tooltip: t`Neurejen seznam`,
    },
    ["ordered-list"]: {
      icon: <ListOrdered size={16} />,
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      tooltip: t`Urejen seznam`,
    },
    ["horizontal-rule"]: {
      icon: <SeparatorHorizontal size={16} />,
      onClick: () => editor.chain().focus().setHorizontalRule().run(),
      tooltip: t`Vodoravna črta`,
    },
    ["paragraph"]: {
      icon: <Pilcrow size={16} />,
      onClick: () => editor.chain().focus().setHardBreak().run(),
      tooltip: t`Novi odstavek`,
    },
    ["undo"]: {
      icon: <Undo size={16} />,
      onClick: () => editor.chain().focus().undo().run(),
      tooltip: t`Razveljavi`,
    },
    ["redo"]: {
      icon: <Redo size={16} />,
      onClick: () => editor.chain().focus().redo().run(),
      tooltip: t`Ponovi`,
    },
  };

  const filteredActions = Object.entries(actions).filter(
    ([key]) =>
      !excludeActions || !excludeActions.includes(key as MarkdownEditorActions),
  );

  if (filteredActions.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex flex-wrap gap-1">
        {filteredActions.map(([key, value]) => (
          <ActionButton key={key} {...value} />
        ))}
      </div>

      <Divider className="my-1" />
    </>
  );
};

const ActionButton = ({
  icon,
  onClick,
  tooltip,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  tooltip?: string;
}) => {
  return (
    <Tooltip
      content={tooltip}
      isDisabled={!tooltip}
      showArrow
      classNames={{
        base: "pointer-events-none",
      }}
    >
      <Button
        onPress={onClick}
        size="sm"
        isIconOnly
        variant="flat"
        type="button"
      >
        {icon}
      </Button>
    </Tooltip>
  );
};

const canUseDom = () =>
  typeof window !== "undefined" && typeof document !== "undefined";

// Run `filter` over every text node in a pasted slice, dropping nodes that
// filter down to nothing, so disallowed characters can't enter via paste.
const filterFragmentText = (
  fragment: Fragment,
  filter: (text: string) => string,
): Fragment => {
  const children: ProseMirrorNode[] = [];
  fragment.forEach((child) => {
    if (child.isText && child.text != null) {
      const filtered = filter(child.text);
      if (filtered) {
        children.push(child.type.schema.text(filtered, child.marks));
      }
      return;
    }
    children.push(child.copy(filterFragmentText(child.content, filter)));
  });
  return Fragment.fromArray(children);
};

const filterSliceText = (
  slice: Slice,
  filter: (text: string) => string,
): Slice =>
  new Slice(
    filterFragmentText(slice.content, filter),
    slice.openStart,
    slice.openEnd,
  );

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildTagRegex = (
  tags: NotificationTagOption[],
  flags: string = "g",
): RegExp | null => {
  const pattern = tags
    .map((tag) => tag.value)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((tagValue) => escapeRegExp(tagValue))
    .join("|");

  if (!pattern) return null;
  return new RegExp(`(${pattern})`, flags);
};

const convertPlainHtmlToEditorHtml = (
  html: string,
  tags: NotificationTagOption[],
) => {
  if (!canUseDom() || !tags.length || !html) {
    return html;
  }

  if (html.includes("data-notification-tag")) {
    return html;
  }

  const detectRegex = buildTagRegex(tags, "");
  const splitRegex = buildTagRegex(tags, "g");
  if (!detectRegex || !splitRegex) {
    return html;
  }

  const container = document.createElement("div");
  container.innerHTML = html;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  const tagValues = tags.map((tag) => tag.value);

  textNodes.forEach((textNode) => {
    if (
      !textNode.nodeValue ||
      (textNode.parentElement &&
        textNode.parentElement.closest("span[data-notification-tag]"))
    ) {
      return;
    }

    if (!detectRegex.test(textNode.nodeValue)) {
      detectRegex.lastIndex = 0;
      return;
    }

    detectRegex.lastIndex = 0;
    const fragments = textNode.nodeValue
      .split(splitRegex)
      .filter((fragment) => fragment.length > 0);

    if (!fragments.length) return;

    const replacements = fragments.map((fragment) => {
      if (tagValues.includes(fragment)) {
        const span = document.createElement("span");
        span.setAttribute("data-notification-tag", "true");
        span.setAttribute("data-value", fragment);
        span.setAttribute("contenteditable", "false");
        span.textContent = getNotificationTagDisplayValue(fragment);
        return span;
      }
      return document.createTextNode(fragment);
    });

    textNode.replaceWith(...replacements);
  });

  // Ensure content is wrapped in a paragraph if it doesn't have block-level elements
  const hasBlockElement = Array.from(container.children).some((child) => {
    const tag = child.tagName.toLowerCase();
    return [
      "p",
      "div",
      "ul",
      "ol",
      "blockquote",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "pre",
    ].includes(tag);
  });

  if (!hasBlockElement && container.childNodes.length > 0) {
    const p = document.createElement("p");
    while (container.firstChild) {
      p.appendChild(container.firstChild);
    }
    container.appendChild(p);
  }

  // Ensure paragraphs with only contenteditable="false" elements have a trailing space
  // This prevents TipTap/ProseMirror from having cursor positioning issues
  const paragraphs = container.querySelectorAll("p");
  paragraphs.forEach((p) => {
    const hasEditableContent = Array.from(p.childNodes).some((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        return true;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        return el.getAttribute("contenteditable") !== "false";
      }
      return false;
    });

    if (!hasEditableContent && p.childNodes.length > 0) {
      // Add a zero-width space to allow cursor positioning
      p.appendChild(document.createTextNode("\u200B"));
    }
  });

  const result = container.innerHTML;

  return result;
};

const stripNotificationTagsFromHtml = (html: string) => {
  if (!canUseDom() || !html) {
    return html;
  }

  const container = document.createElement("div");
  container.innerHTML = html;

  const elements = container.querySelectorAll("span[data-notification-tag]");
  elements.forEach((element) => {
    const value =
      element.getAttribute("data-value") ?? element.textContent ?? "";
    const textNode = document.createTextNode(value);
    element.replaceWith(textNode);
  });

  // Strip the zero-width spaces that `convertPlainHtmlToEditorHtml`
  // injects into whitespace-only paragraphs for cursor positioning.
  // They survive in `editor.getHTML()` but are absent from the prop
  // value derived from plain text, so leaving them in would make the
  // sync effect's comparison mismatch on every keystroke and call
  // `setContent`, which resets the selection to the end of the doc.
  return container.innerHTML.replace(/\u200B/g, "");
};
